import React, { useState, useEffect, useCallback, useRef } from 'react';
import useApi from '../hooks/useApi';
import { Download, Upload, Server, HardDrive, AlertTriangle, CheckCircle, Info, Users } from 'lucide-react';

const BackupPage = ({ currentUser }) => {
    const { apiFetch, token } = useApi(); 
    const [isCreating, setIsCreating] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [isFetchingEntities, setIsFetchingEntities] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [restoreFile, setRestoreFile] = useState(null);
    const [restoreType, setRestoreType] = useState('full'); // 'full', 'entity', ou 'clients_only'
    const [entities, setEntities] = useState([]);
    const [selectedEntityIdentifier, setSelectedEntityIdentifier] = useState('');
    const fileInputRef = useRef(null);

    // Busca entidades se o utilizador for super user
    const fetchEntities = useCallback(async () => {
        if (!currentUser?.is_super_user) return;
        setIsFetchingEntities(true);
        try {
            const data = await apiFetch('/entities');
            setEntities(data || []);
            
            if (data && data.length > 0 && !selectedEntityIdentifier) {
                setSelectedEntityIdentifier(data[0].identifier);
            }
        } catch (err) {
            setError(`Erro ao buscar lista de entidades: ${err.message}`);
        } finally {
            setIsFetchingEntities(false);
        }
    }, [apiFetch, currentUser, selectedEntityIdentifier]);

    useEffect(() => {
        fetchEntities();
    }, [fetchEntities]);


    const handleCreateBackup = async () => {
        setIsCreating(true);
        setError('');
        setSuccess('');
        try {
            const response = await fetch('/api/backup/create', { 
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}` 
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: response.statusText }));
                throw new Error(errorData.error || `Erro ${response.status}: ${response.statusText}`);
            }

            const blob = await response.blob();
            const contentDisposition = response.headers.get('content-disposition');
            let filename = `dfinai_backup_${new Date().toISOString()}.zip`; 
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                if (filenameMatch && filenameMatch.length === 2) {
                    filename = filenameMatch[1];
                }
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
            setSuccess('Backup criado e download iniciado com sucesso.');

        } catch (err) {
            setError(`Erro ao criar backup: ${err.message}`);
        } finally {
            setIsCreating(false);
        }
    };

    const handleFileChange = (event) => {
        setRestoreFile(event.target.files[0]);
        setError('');
        setSuccess('');
    };

    const handleRestoreBackup = async (e) => {
        e.preventDefault();
        if (!restoreFile) {
            setError('Por favor, selecione um arquivo de backup (.zip).');
            return;
        }
        
        // Validação: 'entity' e 'clients_only' exigem selecionar uma entidade
        if ((restoreType === 'entity' || restoreType === 'clients_only') && !selectedEntityIdentifier) {
            setError('Por favor, selecione a entidade para restaurar.');
            return;
        }

        let confirmMessage = `ATENÇÃO: Esta ação substituirá os dados existentes pelos dados do backup.\n\n`;
        
        if (restoreType === 'full') {
            confirmMessage += `TODOS os dados do sistema serão substituídos (incluindo bancos de dados de Teste e Produção).`;
        } else if (restoreType === 'entity') {
            confirmMessage += `Os dados da entidade selecionada E os dados GLOBAIS (utilizadores, entidades, moedas, etc.) serão substituídos.`;
        } else if (restoreType === 'clients_only') {
            confirmMessage += `APENAS o cadastro de clientes e as fotos da entidade selecionada serão substituídos. Os outros dados (Financeiro, OS, etc.) serão mantidos.`;
        }

        confirmMessage += `\n\nTem a certeza que deseja continuar? Recomenda-se criar um backup ANTES de restaurar.`;

        const confirmRestore = window.confirm(confirmMessage);

        if (!confirmRestore) return;

        setIsRestoring(true);
        setError('');
        setSuccess('');

        const formData = new FormData();
        formData.append('backupFile', restoreFile);
        formData.append('restoreType', restoreType);
        
        if (restoreType === 'entity' || restoreType === 'clients_only') {
            formData.append('entityIdentifier', selectedEntityIdentifier);
        }

        try {
            const result = await apiFetch('/backup/restore', {
                method: 'POST',
                body: formData, 
            });
            setSuccess(result.message || 'Restauração concluída com sucesso. Reinicie o servidor se necessário.');
            setRestoreFile(null); 
            if (fileInputRef.current) {
                fileInputRef.current.value = ''; 
            }
        } catch (err) {
            setError(`Erro ao restaurar backup: ${err.message}`);
        } finally {
            setIsRestoring(false);
        }
    };

    if (!currentUser?.is_super_user) {
        return (
            <div className="p-4 text-center text-red-600">
                <AlertTriangle className="mx-auto mb-2" size={48} />
                <h1 className="text-xl font-bold">Acesso Negado</h1>
                <p>Apenas Super Usuários podem aceder a esta funcionalidade.</p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <h1 className="text-3xl font-bold text-gray-800">Backup e Restauração</h1>

            {error && (
                <div className="p-4 text-red-800 bg-red-100 border-l-4 border-red-500 rounded-r-lg" role="alert">
                    <p className="font-bold">Erro</p>
                    <p>{error}</p>
                </div>
            )}
            {success && (
                 <div className="p-4 text-green-800 bg-green-100 border-l-4 border-green-500 rounded-r-lg" role="alert">
                    <p className="font-bold">Sucesso</p>
                    <p>{success}</p>
                </div>
            )}

            {/* Secção de Criar Backup */}
            <div className="p-6 bg-white rounded-lg shadow">
                <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-700 mb-4">
                    <Download size={20} /> Criar Backup do Sistema
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                    Crie um arquivo .zip contendo todos os dados do sistema (base de dados principal, dados de todas as entidades, bancos de teste e arquivos de upload). Guarde este arquivo em local seguro.
                </p>
                <div className="flex items-center gap-2 mb-4 text-xs font-medium text-blue-700 bg-blue-50 p-2 rounded border border-blue-100">
                    <Info size={14} /> <span>O sistema é híbrido. O backup gerado incluirá automaticamente os bancos de dados de produção e os bancos de dados do ambiente de teste (se existirem).</span>
                </div>
                <button
                    onClick={handleCreateBackup}
                    disabled={isCreating || isRestoring}
                    className="inline-flex items-center justify-center px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed"
                >
                    {isCreating ? (
                        <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            A criar backup...
                        </>
                    ) : (
                         <> <Download size={18} className="mr-2" /> Criar e Baixar Backup </>
                    )}
                </button>
            </div>

            {/* Secção de Restaurar Backup */}
            <div className="p-6 bg-white rounded-lg shadow">
                <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-700 mb-4">
                     <Upload size={20} /> Restaurar Backup
                </h2>
                <div className="p-4 mb-4 text-yellow-800 bg-yellow-100 border-l-4 border-yellow-500 rounded-r-lg" role="alert">
                    <p className="font-bold flex items-center gap-1"><AlertTriangle size={16}/> Atenção!</p>
                    <p className="text-sm">A restauração substituirá os dados existentes pelos dados do backup. Recomenda-se fortemente criar um backup antes de prosseguir.</p>
                </div>

                 <form onSubmit={handleRestoreBackup} className="space-y-4">
                    <div>
                        <label htmlFor="backupFile" className="block text-sm font-medium text-gray-700">Arquivo de Backup (.zip)</label>
                        <input
                            type="file"
                            id="backupFile"
                            accept=".zip"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            required
                            className="w-full px-3 py-2 mt-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                    </div>

                    <fieldset className="space-y-2">
                        <legend className="text-sm font-medium text-gray-700">Tipo de Restauração</legend>
                        <div className="flex flex-col gap-2 mt-1">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="restoreType"
                                    value="full"
                                    checked={restoreType === 'full'}
                                    onChange={() => setRestoreType('full')}
                                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="ml-2 text-sm text-gray-700 flex items-center gap-2"><Server size={16}/> Sistema Completo (Substitui Produção e Teste)</span>
                            </label>
                            
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="restoreType"
                                    value="clients_only"
                                    checked={restoreType === 'clients_only'}
                                    onChange={() => setRestoreType('clients_only')}
                                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="ml-2 text-sm text-gray-700 flex items-center gap-2 font-medium"><Users size={16}/> Apenas Clientes (Dados + Fotos)</span>
                            </label>

                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="restoreType"
                                    value="entity"
                                    checked={restoreType === 'entity'}
                                    onChange={() => setRestoreType('entity')}
                                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                />
                                <span className="ml-2 text-sm text-gray-700 flex items-center gap-2"><HardDrive size={16}/> Entidade Específica (Substitui DB da entidade e globais)</span>
                            </label>
                        </div>
                    </fieldset>

                    {/* Exibe o seletor de entidade para 'entity' OU 'clients_only' */}
                    {(restoreType === 'entity' || restoreType === 'clients_only') && (
                        <div className="bg-gray-50 p-4 rounded-md border border-gray-200 animate-in fade-in slide-in-from-top-2 duration-200">
                            <label htmlFor="entitySelect" className="block text-sm font-medium text-gray-700">
                                {restoreType === 'clients_only' ? 'Selecione a Entidade para Restaurar os Clientes' : 'Selecione a Entidade Atual a ser Substituída'}
                            </label>
                            <select
                                id="entitySelect"
                                value={selectedEntityIdentifier}
                                onChange={(e) => setSelectedEntityIdentifier(e.target.value)}
                                required
                                disabled={isFetchingEntities || entities.length === 0}
                                className="w-full p-2 mt-1 border border-gray-300 rounded-md disabled:bg-gray-100"
                            >
                                {isFetchingEntities ? (
                                    <option value="">A carregar entidades...</option>
                                ) : entities.length === 0 ? (
                                    <option value="">Nenhuma entidade encontrada.</option>
                                ) : (
                                    entities.map(entity => (
                                        <option key={entity.id} value={entity.identifier}>
                                            {entity.name} ({entity.identifier})
                                        </option>
                                    ))
                                )}
                            </select>
                            <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                                <Info size={12}/> 
                                {restoreType === 'clients_only' 
                                    ? 'Apenas a tabela de clientes e as fotos serão substituídas nesta entidade.' 
                                    : 'Os dados desta entidade (e os dados globais) serão totalmente substituídos.'}
                            </p>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isFetchingEntities || isCreating || isRestoring || !restoreFile || ((restoreType === 'entity' || restoreType === 'clients_only') && !selectedEntityIdentifier)}
                        className="inline-flex items-center justify-center px-4 py-2 font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-300 disabled:cursor-not-allowed"
                    >
                         {isRestoring ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                A restaurar...
                            </>
                         ) : (
                             <> <Upload size={18} className="mr-2" /> Restaurar Backup </>
                         )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default BackupPage;